import { JsonObject } from 'survey-core';
import { SurveyCreator } from 'survey-creator-react';
import CustomWidget from 'vl-md/src/CustomSurvey/customWidgets/CustomWidget';
import { useSystemType } from '@vl-core/hooks/useSystemType';

/** Renders a responsive embedded youtube video player. */
const CustomizeSurveyYouTube = (surveyCreator?: SurveyCreator) => {
  const systemType = useSystemType();
  const activatedByChangeHandler = () => {
    JsonObject.metaData.addClass('youtube', [], null, 'empty');
    JsonObject.metaData.removeProperty('youtube', 'contentMode');
    JsonObject.metaData.addProperties('youtube', [
      {
        name: 'VideoId',
        default: '6BdCJDwkEBg',
        category: 'general',
        visibleIndex: 2
      },
      {
        name: 'VideoHeight',
        type: 'minmaxnumber',
        choices: [50, 1080],
        default: 390,
        category: 'general',
        visibleIndex: 3
      },
      {
        name: 'VideoWidth',
        type: 'minmaxnumber',
        choices: [50, 1920],
        default: 640,
        category: 'general',
        visibleIndex: 4
      }
    ]);
  };

  const afterRenderHandler = (question, el) => {
    const message = el.getElementsByTagName('div')[0];
    let player = null;
    const hasAcceptedFunctionalCookies =
      localStorage?.getItem('cookie-consent') === 'all' || localStorage?.getItem('cookie-consent')?.includes('fun');
    if (systemType === 'vl-app' && !hasAcceptedFunctionalCookies) {
      const existingPlaceholder = document.getElementById('survey-yt-placeholder');
      if (!existingPlaceholder) {
        const placeholder = document.createElement('div');
        placeholder.innerHTML = `
          <div
            style="padding: 1em;
                    border: 1px solid #ccc;
                    width: ${question.VideoWidth}px;
                    height: ${question.VideoHeight}px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;"
            id="survey-yt-placeholder">
              <p>To view this YouTube video, you will need to accept functional cookies.</p>
              <button
                id="accept-cookies-survey-btn"
                style="padding: 4px;"
                class="ant-btn ant-btn-primary ant-btn-color-primary ant-btn-variant-solid">
                  Accept Cookies
              </button>
          </div>
        `;
        el.appendChild(placeholder);
        el.querySelector('#accept-cookies-survey-btn')?.addEventListener('click', () => {
          question?.survey?.showCookieSettings?.();
        });
      }
      return;
    }

    import('youtube-iframe').then((module) => {
      const YouTubeIframeLoader = module.default;
      YouTubeIframeLoader.load((YT) => {
        const onPlayerStateChange = (event) => {
          player = event.target;
          const timestampNow = new Date().getTime();

          switch (event.data) {
            case YT.PlayerState.PLAYING:
              question.started = timestampNow;
              break;
            case YT.PlayerState.PAUSED:
            case YT.PlayerState.ENDED:
              if (question.value) {
                question.value = (parseFloat(question.value) + (timestampNow - question.started) / 1000).toFixed(2);
              } else {
                question.value = ((timestampNow - question.started) / 1000).toFixed(2);
              }
              break;
            default:
              throw new Error('Unhandled YT value');
          }
        };

        player = new YT.Player(el, {
          height: question.VideoHeight,
          width: question.VideoWidth,
          videoId: question.VideoId,
          host: 'https://www.youtube-nocookie.com',
          origin: window.location.origin,
          events: {
            onStateChange: onPlayerStateChange
          },
          playerVars: {
            rel: 0,
            showinfo: 0,
            controls: 1,
            disablekb: 1,
            modestbranding: 1,
            cc_load_policy: 1,
            playsinline: 1
          }
        });
      });
    });

    const onValueChangedCallback = () => {
      if (!player) {
        return;
      }
      if (question.VideoWidth && question.VideoHeight) {
        player.setSize?.(question.VideoWidth, question.VideoHeight);
      }
      if (question.VideoId) {
        if (question.VideoId !== player.getVideoData?.().video_id) {
          player.cueVideoById?.(question.VideoId);
        }
      } else {
        player.cueVideoById?.('6BdCJDwkEBg');
      }
    };
    const onReadOnlyChangedCallback = () => {
      if (question.isReadOnly) {
        message.setAttribute('disabled', 'disabled');
      } else {
        message.removeAttribute('disabled');
      }
    };
    question.readOnlyChangedCallback = onReadOnlyChangedCallback;
    question.onPropertyValueChanged = onValueChangedCallback;
    onReadOnlyChangedCallback();
  };

  const widgetName = 'youtube';
  const widgetTitle = 'YouTube';
  const widgetIconName = 'hbs-youtube-class';
  const htmlTemplate = '<div data-testid="customized-youtube-component"><div></div></div>';

  CustomWidget({
    name: widgetName,
    title: widgetTitle,
    iconName: widgetIconName,
    activatedByChangeHandler,
    htmlTemplate,
    afterRenderHandler,
    surveyCreator
  });
};

export default CustomizeSurveyYouTube;
